/**
 * Alshallal Pastry Company HR System - System Reset Page (Data-Only)
 * Phase 24.9: Test Data Reset Feature
 * 
 * PURPOSE: Reset operational/test data ONLY. Never touches code, templates, or system config.
 * 
 * SECURITY: Super Admin ONLY
 * Multiple confirmation steps required:
 * 1. Preview data counts (operational vs preserved)
 * 2. Acknowledge understanding checkbox
 * 3. Enter email
 * 4. Enter password  
 * 5. Type confirmation phrase "RESET TEST DATA"
 * 
 * PRESERVED (NEVER DELETED):
 * - letter_templates, document_templates, document_types
 * - leave_types, request_types
 * - company_settings, system_settings, module_policy
 * - departments, branches
 * - audit_logs
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Trash2,
  ShieldAlert,
  Loader2,
  CheckCircle2,
  XCircle,
  Lock,
  Mail,
  Eye,
  EyeOff,
  AlertOctagon,
  Shield,
  Database,
  FileText,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const CONFIRMATION_PHRASE = 'RESET TEST DATA';

// Collection display names
const COLLECTION_LABELS = {
  users: { en: 'Users', ar: 'المستخدمون' },
  employees: { en: 'Employees', ar: 'الموظفون' },
  documents: { en: 'Employee Documents', ar: 'مستندات الموظفين' },
  employee_documents: { en: 'Additional Docs', ar: 'مستندات إضافية' },
  company_documents: { en: 'Company Documents', ar: 'مستندات الشركة' },
  letter_documents: { en: 'Issued Letters', ar: 'الخطابات المصدرة' },
  letter_requests: { en: 'Letter Requests', ar: 'طلبات الخطابات' },
  leave_requests: { en: 'Leave Requests', ar: 'طلبات الإجازات' },
  leave_balances: { en: 'Leave Balances', ar: 'أرصدة الإجازات' },
  requests: { en: 'Requests', ar: 'الطلبات' },
  request_attachments: { en: 'Attachments', ar: 'المرفقات' },
  pending_employee_updates: { en: 'Pending Updates', ar: 'التحديثات المعلقة' },
  notification_deliveries: { en: 'Notifications', ar: 'الإشعارات' },
  email_broadcasts: { en: 'Email Broadcasts', ar: 'البث البريدي' },
  import_batches: { en: 'Import Jobs', ar: 'مهام الاستيراد' },
  ai_conversations: { en: 'AI Conversations', ar: 'محادثات الذكاء' },
  ai_transcripts: { en: 'AI Transcripts', ar: 'نصوص الذكاء' },
  archive_records: { en: 'Archives', ar: 'الأرشيف' },
  user_sessions: { en: 'Sessions', ar: 'الجلسات' },
  ocr_jobs: { en: 'OCR Jobs', ar: 'مهام OCR' },
  // Preserved
  letter_templates: { en: 'Letter Templates', ar: 'قوالب الخطابات' },
  document_templates: { en: 'Document Templates', ar: 'قوالب المستندات' },
  document_types: { en: 'Document Types', ar: 'أنواع المستندات' },
  leave_types: { en: 'Leave Types', ar: 'أنواع الإجازات' },
  request_types: { en: 'Request Types', ar: 'أنواع الطلبات' },
  company_settings: { en: 'Company Settings', ar: 'إعدادات الشركة' },
  system_settings: { en: 'System Settings', ar: 'إعدادات النظام' },
  module_policy: { en: 'Module Policy', ar: 'سياسة الوحدات' },
  departments: { en: 'Departments', ar: 'الأقسام' },
  branches: { en: 'Branches', ar: 'الفروع' },
  audit_logs: { en: 'Audit Logs', ar: 'سجلات التدقيق' },
  ai_knowledge_base: { en: 'AI Knowledge', ar: 'قاعدة المعرفة' },
};

const getCollectionLabel = (key, isRTL) => {
  const labels = COLLECTION_LABELS[key];
  if (labels) return isRTL ? labels.ar : labels.en;
  return key.replace(/_/g, ' ');
};

const SystemResetPage = () => {
  const { token, user, hasRole } = useAuth();
  const { isRTL } = useLanguage();
  
  // Access check
  const isSuperAdmin = hasRole('super_admin');
  
  // State
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [resetInProgress, setResetInProgress] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [environmentBlocked, setEnvironmentBlocked] = useState(false);
  
  // Form state
  const [understandCheck, setUnderstandCheck] = useState(false);
  
  const [confirmation, setConfirmation] = useState({
    email: '',
    password: '',
    confirmation_phrase: '',
  });
  
  const [errors, setErrors] = useState({});
  
  // Fetch preview data
  const fetchPreview = useCallback(async () => {
    if (!isSuperAdmin) return;
    
    setLoading(true);
    setEnvironmentBlocked(false);
    try {
      const response = await axios.get(`${API_URL}/system/reset/preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewData(response.data);
    } catch (err) {
      console.error('Failed to fetch preview:', err);
      if (err.response?.data?.detail?.code === 'SYSTEM_RESET_DISABLED_IN_PRODUCTION') {
        setEnvironmentBlocked(true);
        toast.error(isRTL ? 'إعادة التعيين معطلة في بيئة الإنتاج' : 'System reset is disabled in production');
      } else {
        toast.error('Failed to load data preview');
      }
    } finally {
      setLoading(false);
    }
  }, [token, isSuperAdmin, isRTL]);
  
  useEffect(() => {
    fetchPreview();
  }, [fetchPreview]);
  
  // Handle confirmation field change
  const handleConfirmationChange = (field, value) => {
    setConfirmation(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: null }));
  };
  
  // Validate step 2 (email)
  const validateEmail = () => {
    if (!confirmation.email) {
      setErrors({ email: isRTL ? 'البريد الإلكتروني مطلوب' : 'Email is required' });
      return false;
    }
    if (confirmation.email.toLowerCase() !== user?.email?.toLowerCase()) {
      setErrors({ email: isRTL ? 'البريد الإلكتروني غير متطابق' : 'Email does not match your account' });
      return false;
    }
    return true;
  };
  
  // Validate step 3 (password)
  const validatePassword = () => {
    if (!confirmation.password) {
      setErrors({ password: isRTL ? 'كلمة المرور مطلوبة' : 'Password is required' });
      return false;
    }
    return true;
  };
  
  // Validate step 4 (confirmation phrase)
  const validatePhrase = () => {
    if (confirmation.confirmation_phrase !== CONFIRMATION_PHRASE) {
      setErrors({ 
        confirmation_phrase: isRTL 
          ? `يجب كتابة "${CONFIRMATION_PHRASE}" بالضبط`
          : `You must type exactly "${CONFIRMATION_PHRASE}"` 
      });
      return false;
    }
    return true;
  };
  
  // Handle next step
  const handleNextStep = () => {
    if (currentStep === 1 && !understandCheck) {
      toast.error(isRTL ? 'يجب تأكيد الفهم أولاً' : 'You must acknowledge understanding first');
      return;
    }
    if (currentStep === 2 && !validateEmail()) return;
    if (currentStep === 3 && !validatePassword()) return;
    if (currentStep === 4 && !validatePhrase()) return;
    
    if (currentStep < 4) {
      setCurrentStep(prev => prev + 1);
    } else {
      executeReset();
    }
  };
  
  // Execute reset
  const executeReset = async () => {
    setResetInProgress(true);
    setErrors({});
    
    try {
      // First validate
      await axios.post(`${API_URL}/system/reset/validate`, confirmation, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Then execute
      const response = await axios.post(`${API_URL}/system/reset/execute`, {
        confirmation
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResetResult(response.data);
      toast.success(isRTL ? 'تم إعادة تعيين البيانات بنجاح' : 'System reset completed successfully');
      
    } catch (err) {
      console.error('Reset failed:', err);
      const errorMsg = err.response?.data?.detail?.message || err.response?.data?.detail?.error || 'Reset failed';
      const errorCode = err.response?.data?.detail?.code;
      
      if (errorCode === 'SYSTEM_RESET_DISABLED_IN_PRODUCTION') {
        setEnvironmentBlocked(true);
        setShowConfirmDialog(false);
        toast.error(isRTL ? 'إعادة التعيين معطلة في بيئة الإنتاج' : 'System reset is disabled in production');
      } else if (errorCode === 'EMAIL_MISMATCH') {
        setCurrentStep(2);
        setErrors({ email: errorMsg });
      } else if (errorCode === 'INVALID_PASSWORD') {
        setCurrentStep(3);
        setErrors({ password: errorMsg });
      } else if (errorCode === 'INVALID_CONFIRMATION') {
        setCurrentStep(4);
        setErrors({ confirmation_phrase: errorMsg });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setResetInProgress(false);
    }
  };
  
  // Reset dialog
  const resetDialog = () => {
    setShowConfirmDialog(false);
    setCurrentStep(1);
    setConfirmation({ email: '', password: '', confirmation_phrase: '' });
    setErrors({});
    setUnderstandCheck(false);
    setResetResult(null);
    fetchPreview();
  };
  
  // Access denied for non-super admin
  if (!isSuperAdmin) {
    return (
      <div className="p-6" data-testid="system-reset-access-denied">
        <Card className="max-w-md mx-auto border-red-200 bg-red-50">
          <CardContent className="pt-6 text-center">
            <ShieldAlert className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-red-800 mb-2">
              {isRTL ? 'وصول مرفوض' : 'Access Denied'}
            </h2>
            <p className="text-red-700">
              {isRTL 
                ? 'فقط المسؤول الأعلى (Super Admin) يمكنه الوصول إلى إعادة تعيين النظام.'
                : 'Only Super Admin can access System Reset. This is a critical security feature.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Environment blocked
  if (environmentBlocked) {
    return (
      <div className="p-6" data-testid="system-reset-blocked">
        <Card className="max-w-md mx-auto border-amber-200 bg-amber-50">
          <CardContent className="pt-6 text-center">
            <Shield className="h-16 w-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-amber-800 mb-2">
              {isRTL ? 'إعادة التعيين معطلة' : 'System Reset Disabled'}
            </h2>
            <p className="text-amber-700">
              {isRTL 
                ? 'إعادة تعيين النظام معطلة في بيئة الإنتاج. اتصل بالمسؤول لتمكينها.'
                : 'System reset is disabled in production environment. Contact administrator to enable ALLOW_SYSTEM_RESET=true.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Reset result view
  if (resetResult) {
    return (
      <div className="p-6" data-testid="system-reset-result">
        <Card className="max-w-3xl mx-auto border-green-200 bg-green-50">
          <CardHeader className="text-center">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <CardTitle className="text-green-800">
              {isRTL ? 'تم إعادة تعيين البيانات بنجاح' : 'System Reset Completed'}
            </CardTitle>
            <CardDescription className="text-green-700">
              {isRTL ? 'تم حذف البيانات التشغيلية. البيانات الأساسية محفوظة.' : 'Operational data deleted. System configuration preserved.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Deleted Data */}
              <div>
                <h3 className="font-semibold text-red-700 mb-2 flex items-center gap-2">
                  <Trash2 className="h-4 w-4" />
                  {isRTL ? 'البيانات المحذوفة' : 'Deleted Data'}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  {Object.entries(resetResult.deleted_counts || {})
                    .filter(([key]) => !key.startsWith('_'))
                    .map(([key, count]) => (
                    <div key={key} className="flex justify-between bg-white p-2 rounded border border-red-100">
                      <span className="text-slate-600">{getCollectionLabel(key, isRTL)}</span>
                      <span className="font-mono font-bold text-red-600">{count}</span>
                    </div>
                  ))}
                </div>
                {resetResult.files_deleted > 0 && (
                  <div className="mt-2 text-sm text-slate-600">
                    {isRTL ? `تم حذف ${resetResult.files_deleted} ملف` : `${resetResult.files_deleted} files cleaned up`}
                  </div>
                )}
              </div>
              
              {/* Preserved Data */}
              <div>
                <h3 className="font-semibold text-green-700 mb-2 flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  {isRTL ? 'البيانات المحفوظة' : 'Preserved Data'}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  {Object.entries(resetResult.preserved_counts || {}).map(([key, count]) => (
                    <div key={key} className="flex justify-between bg-white p-2 rounded border border-green-100">
                      <span className="text-slate-600">{getCollectionLabel(key, isRTL)}</span>
                      <span className="font-mono font-bold text-green-600">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Audit Info */}
              <div className="bg-slate-100 rounded p-3 text-sm text-slate-600">
                <p><strong>{isRTL ? 'معرف التدقيق:' : 'Audit ID:'}</strong> {resetResult.audit_log_id}</p>
                <p><strong>{isRTL ? 'تم بواسطة:' : 'Performed by:'}</strong> {resetResult.performed_by}</p>
                <p><strong>{isRTL ? 'الوقت:' : 'Time:'}</strong> {new Date(resetResult.timestamp).toLocaleString()}</p>
              </div>
              
              <div className="text-center mt-6">
                <Button onClick={resetDialog} variant="outline" className="gap-2">
                  <RefreshCw className="h-4 w-4" />
                  {isRTL ? 'إغلاق وتحديث' : 'Close & Refresh'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className={cn("p-6 space-y-6", isRTL && "rtl")} data-testid="system-reset-page">
      {/* Warning Header */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-4">
        <AlertOctagon className="h-8 w-8 text-red-500 flex-shrink-0 mt-1" />
        <div>
          <h2 className="text-lg font-bold text-red-800 mb-1">
            {isRTL ? '⚠️ تحذير: إعادة تعيين بيانات الاختبار' : '⚠️ Warning: Test Data Reset'}
          </h2>
          <p className="text-red-700 text-sm">
            {isRTL 
              ? 'هذه العملية ستحذف جميع البيانات التشغيلية (الموظفين، المستندات، المستخدمين، الطلبات) بشكل دائم. القوالب وإعدادات النظام والأقسام والفروع ستبقى محفوظة.'
              : 'This operation will permanently delete all operational data (employees, documents, users, requests). Templates, system settings, departments, and branches will be preserved.'}
          </p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Data to DELETE */}
        <Card className="border-red-200">
          <CardHeader className="bg-red-50 rounded-t-lg">
            <CardTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" />
              {isRTL ? 'البيانات التي ستُحذف' : 'Data to be DELETED'}
            </CardTitle>
            <CardDescription className="text-red-600">
              {isRTL ? 'البيانات التشغيلية وبيانات الاختبار' : 'Operational and test data'}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : previewData ? (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(previewData.operational_counts || {})
                    .filter(([_, count]) => count > 0)
                    .map(([key, count]) => (
                    <div 
                      key={key} 
                      className="flex justify-between p-2 rounded-lg bg-red-50 border border-red-100"
                    >
                      <span className="text-sm text-slate-700">{getCollectionLabel(key, isRTL)}</span>
                      <span className="font-mono font-bold text-red-600">{count}</span>
                    </div>
                  ))}
                </div>
                <div className="pt-2 border-t border-red-200 flex justify-between font-semibold">
                  <span className="text-red-700">{isRTL ? 'الإجمالي للحذف' : 'Total to Delete'}</span>
                  <span className="font-mono text-red-700">{previewData.total_to_delete}</span>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
        
        {/* Data to PRESERVE */}
        <Card className="border-green-200">
          <CardHeader className="bg-green-50 rounded-t-lg">
            <CardTitle className="flex items-center gap-2 text-green-700">
              <Shield className="h-5 w-5" />
              {isRTL ? 'البيانات المحفوظة (لن تُحذف)' : 'Data PRESERVED (Not Deleted)'}
            </CardTitle>
            <CardDescription className="text-green-600">
              {isRTL ? 'القوالب والإعدادات والبيانات الأساسية' : 'Templates, settings, and master data'}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : previewData ? (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(previewData.preserved_counts || {}).map(([key, count]) => (
                    <div 
                      key={key} 
                      className="flex justify-between p-2 rounded-lg bg-green-50 border border-green-100"
                    >
                      <span className="text-sm text-slate-700">{getCollectionLabel(key, isRTL)}</span>
                      <span className="font-mono font-bold text-green-600">{count}</span>
                    </div>
                  ))}
                </div>
                <div className="pt-2 border-t border-green-200 flex justify-between font-semibold">
                  <span className="text-green-700">{isRTL ? 'الإجمالي المحفوظ' : 'Total Preserved'}</span>
                  <span className="font-mono text-green-700">{previewData.total_preserved}</span>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
      
      {/* Understanding Note */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <FileText className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <p className="font-semibold mb-1">{isRTL ? 'ما الذي يبقى محفوظاً؟' : 'What stays preserved?'}</p>
              <ul className="list-disc list-inside space-y-1 text-amber-700">
                <li>{isRTL ? 'قوالب الخطابات والمستندات' : 'Letter and document templates'}</li>
                <li>{isRTL ? 'أنواع الإجازات والطلبات' : 'Leave and request types'}</li>
                <li>{isRTL ? 'إعدادات الشركة والنظام' : 'Company and system settings'}</li>
                <li>{isRTL ? 'الأقسام والفروع' : 'Departments and branches'}</li>
                <li>{isRTL ? 'سجلات التدقيق' : 'Audit logs'}</li>
                <li>{isRTL ? 'حسابك الحالي (Super Admin)' : 'Your account (Super Admin)'}</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Reset Button */}
      <div className="flex justify-center">
        <Button
          size="lg"
          variant="destructive"
          onClick={() => setShowConfirmDialog(true)}
          className="gap-2 px-8"
          data-testid="btn-start-reset"
        >
          <Trash2 className="h-5 w-5" />
          {isRTL ? 'بدء إعادة تعيين البيانات' : 'Start Data Reset'}
        </Button>
      </div>
      
      {/* Multi-Step Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="max-w-md" data-testid="reset-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <ShieldAlert className="h-5 w-5" />
              {isRTL ? 'تأكيد إعادة تعيين البيانات' : 'Confirm Data Reset'}
              <span className="text-sm font-normal text-slate-500">
                ({currentStep}/4)
              </span>
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isRTL 
                ? 'هذا سيحذف بيانات الاختبار التشغيلية فقط. القوالب والإعدادات ستبقى.'
                : 'This will delete operational test data only. Templates and settings will remain.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-4">
            {/* Step 1: Warning + Checkbox */}
            {currentStep === 1 && (
              <div className="space-y-4" data-testid="step-1-warning">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
                  <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
                  <p className="text-center font-semibold mb-2">
                    {isRTL ? 'تحذير خطير!' : 'Critical Warning!'}
                  </p>
                  <p className="text-sm text-center">
                    {isRTL 
                      ? 'أنت على وشك حذف جميع البيانات التشغيلية بشكل دائم. هذا الإجراء لا يمكن التراجع عنه.'
                      : 'You are about to permanently delete all operational data. This action cannot be undone.'}
                  </p>
                </div>
                
                <div className="flex items-start space-x-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <Checkbox
                    id="understand-check"
                    checked={understandCheck}
                    onCheckedChange={setUnderstandCheck}
                    className="mt-0.5"
                    data-testid="checkbox-understand"
                  />
                  <Label htmlFor="understand-check" className="text-sm text-amber-800 cursor-pointer">
                    {isRTL 
                      ? 'أفهم أن هذا سيحذف بيانات الاختبار ولا يمكن التراجع عن هذا الإجراء'
                      : 'I understand this will delete test data and this action cannot be undone'}
                  </Label>
                </div>
              </div>
            )}
            
            {/* Step 2: Email */}
            {currentStep === 2 && (
              <div className="space-y-4" data-testid="step-2-email">
                <Label className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  {isRTL ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
                </Label>
                <Input
                  type="email"
                  value={confirmation.email}
                  onChange={(e) => handleConfirmationChange('email', e.target.value)}
                  placeholder={user?.email}
                  className={cn(errors.email && "border-red-500")}
                  data-testid="input-confirm-email"
                />
                {errors.email && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <XCircle className="h-4 w-4" /> {errors.email}
                  </p>
                )}
                <p className="text-xs text-slate-500">
                  {isRTL ? `يجب أن يتطابق مع: ${user?.email}` : `Must match: ${user?.email}`}
                </p>
              </div>
            )}
            
            {/* Step 3: Password */}
            {currentStep === 3 && (
              <div className="space-y-4" data-testid="step-3-password">
                <Label className="flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  {isRTL ? 'أدخل كلمة المرور' : 'Enter your password'}
                </Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={confirmation.password}
                    onChange={(e) => handleConfirmationChange('password', e.target.value)}
                    className={cn(errors.password && "border-red-500", "pe-10")}
                    data-testid="input-confirm-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute end-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <XCircle className="h-4 w-4" /> {errors.password}
                  </p>
                )}
              </div>
            )}
            
            {/* Step 4: Confirmation Phrase */}
            {currentStep === 4 && (
              <div className="space-y-4" data-testid="step-4-phrase">
                <Label>
                  {isRTL ? 'اكتب العبارة التالية للتأكيد:' : 'Type the following phrase to confirm:'}
                </Label>
                <div className="bg-red-100 border border-red-300 rounded p-3 text-center font-mono font-bold text-red-800">
                  {CONFIRMATION_PHRASE}
                </div>
                <Input
                  type="text"
                  value={confirmation.confirmation_phrase}
                  onChange={(e) => handleConfirmationChange('confirmation_phrase', e.target.value.toUpperCase())}
                  placeholder={CONFIRMATION_PHRASE}
                  className={cn(
                    errors.confirmation_phrase && "border-red-500",
                    "font-mono text-center"
                  )}
                  data-testid="input-confirm-phrase"
                />
                {errors.confirmation_phrase && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <XCircle className="h-4 w-4" /> {errors.confirmation_phrase}
                  </p>
                )}
              </div>
            )}
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel onClick={resetDialog} disabled={resetInProgress}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </AlertDialogCancel>
            
            {currentStep > 1 && (
              <Button
                variant="outline"
                onClick={() => setCurrentStep(prev => prev - 1)}
                disabled={resetInProgress}
              >
                {isRTL ? 'رجوع' : 'Back'}
              </Button>
            )}
            
            <Button
              variant="destructive"
              onClick={handleNextStep}
              disabled={resetInProgress || (currentStep === 1 && !understandCheck)}
              data-testid="btn-confirm-step"
            >
              {resetInProgress ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin me-2" />
                  {isRTL ? 'جاري الحذف...' : 'Resetting...'}
                </>
              ) : currentStep === 4 ? (
                isRTL ? '🗑️ إعادة تعيين نهائي' : '🗑️ Execute Reset'
              ) : (
                isRTL ? 'متابعة' : 'Continue'
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SystemResetPage;
